<template>
  <div class="ai-check-container">
    <!-- Header Banner -->
    <div class="ai-header-banner">
      <div class="banner-overlay"></div>
      <div class="banner-content">
        <div class="banner-left">
          <div class="ai-icon-pulse">
            <i class="el-icon-cpu"></i>
          </div>
          <div>
            <h1 class="banner-title">HỆ THỐNG KIỂM TRA THÔNG TIN NHẬN DIỆN AI</h1>
            <p class="banner-subtitle">Truy vấn, đối soát dữ liệu khuôn mặt, vân tay và thẻ trên các thiết bị nhận diện AI toàn hệ thống.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats Grid -->
    <div class="ai-stats-grid">
      <div class="stat-card blue">
        <div class="stat-icon"><i class="el-icon-user-solid"></i></div>
        <div class="stat-info">
          <p class="stat-label">Tổng người dùng AI (trang này)</p>
          <h3 class="stat-value">{{ dataTable.length }} / {{ totalElements }}</h3>
        </div>
      </div>
      <div class="stat-card green">
        <div class="stat-icon"><i class="el-icon-bank-card"></i></div>
        <div class="stat-info">
          <p class="stat-label">Tổng số thẻ (trang này)</p>
          <h3 class="stat-value">{{ stats.totalCards }}</h3>
        </div>
      </div>
      <div class="stat-card purple">
        <div class="stat-icon"><i class="el-icon-thumb"></i></div>
        <div class="stat-info">
          <p class="stat-label">Tổng số vân tay (trang này)</p>
          <h3 class="stat-value">{{ stats.totalFingers }}</h3>
        </div>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="glass-card mb-4">
      <el-form :inline="true" :model="dataFilter" class="modern-filter-form">
        <el-form-item label="Trường học">
          <el-select
            v-model="dataFilter.idSchool"
            filterable
            clearable
            placeholder="Tất cả các trường"
            class="modern-select"
            @change="handleSchoolChange"
          >
            <el-option
              v-for="item in schoolList"
              :key="item.id"
              :value="item.id"
              :label="item.name"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Thiết bị">
          <el-select
            v-model="dataFilter.idDevice"
            filterable
            clearable
            :placeholder="dataFilter.idSchool ? 'Tất cả thiết bị' : 'Chọn trường trước'"
            :disabled="!dataFilter.idSchool"
            class="modern-select"
            @change="handleSearch"
          >
            <el-option
              v-for="item in deviceList"
              :key="item.id"
              :value="item.id"
              :label="item.nameDevice"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="Loại tài khoản">
          <el-select
            v-model="dataFilter.typeAccount"
            clearable
            placeholder="Tất cả loại"
            class="modern-select"
            @change="handleSearch"
          >
            <el-option label="Giáo viên" value="teacher" />
            <el-option label="Học sinh" value="parent" />
          </el-select>
        </el-form-item>

        <el-form-item label="Tìm kiếm nhanh">
          <el-input
            v-model="dataFilter.query"
            clearable
            placeholder="Tên, Employee No, User ID..."
            class="modern-input"
            prefix-icon="el-icon-search"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
        </el-form-item>

        <el-form-item class="filter-actions">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">Tìm kiếm</el-button>
          <el-button icon="el-icon-refresh" @click="resetFilters">Làm mới</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- Main Table -->
    <div class="glass-card table-container">
      <el-table
        :data="dataTable"
        v-loading="loading"
        element-loading-text="Đang quét thông tin nhận diện AI..."
        element-loading-spinner="el-icon-loading"
        element-loading-background="rgba(255,255,255,0.7)"
        class="modern-table"
        border
        highlight-current-row
      >
        <el-table-column type="index" :index="calculateStt" label="STT" width="50" align="center" />
        
        <el-table-column label="Tên trường" min-width="180">
          <template slot-scope="{ row }">
            <span class="school-name-text">{{ row.schoolName || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Thông tin nhân sự" min-width="200">
          <template slot-scope="{ row }">
            <div class="user-profile-cell">
              <el-image
                :src="row.urlWebFace"
                fit="cover"
                class="user-avatar"
              >
                <div slot="error" class="img-error-placeholder">
                  <i class="el-icon-user-solid"></i>
                </div>
              </el-image>
              <div class="user-details">
                <span class="user-name">{{ row.name }}</span>
                <el-tag :type="getAccountTagType(row.typeAccount)" size="mini" effect="dark" class="user-tag">
                  {{ getAccountLabel(row.typeAccount) }}
                </el-tag>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Mã đối chiếu" width="200">
          <template slot-scope="{ row }">
            <div class="info-list">
              <div><span class="info-lbl">ID Onekids:</span> <strong>{{ row.idUser }}</strong></div>
              <div><span class="info-lbl">ID Thiết bị:</span> <strong>{{ row.deviceRecognition ? row.deviceRecognition.id : '—' }}</strong></div>
              <div><span class="info-lbl">Mã NV:</span> <strong>{{ row.employeeNo || '—' }}</strong></div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Tên thiết bị" min-width="160">
          <template slot-scope="{ row }">
            <span class="device-name-text">
              <i class="el-icon-s-platform" style="color: #64748b; margin-right: 4px;"></i>
              {{ row.deviceRecognition ? row.deviceRecognition.name : '—' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Phương thức nhận diện" width="180" align="center">
          <template slot-scope="{ row }">
            <div class="credentials-badges">
              <span class="badge-item" :class="{ active: row.urlWebFace }">
                <i class="el-icon-picture"></i> Face
              </span>
              <span class="badge-item" :class="{ active: row.cardRecognitionList && row.cardRecognitionList.length > 0 }">
                <i class="el-icon-bank-card"></i> {{ row.cardRecognitionList ? row.cardRecognitionList.length : 0 }} Card
              </span>
              <span class="badge-item" :class="{ active: row.fingerRecognitionList && row.fingerRecognitionList.length > 0 }">
                <i class="el-icon-thumb"></i> {{ row.fingerRecognitionList ? row.fingerRecognitionList.length : 0 }} Finger
              </span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Tác vụ" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="primary" size="mini" icon="el-icon-view" @click="handleShowDetail(row)">
              Chi tiết
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Pagination controls -->
      <div class="pagination-wrapper mt-3 flex justify-center">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page.sync="pageIndex"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="totalElements"
        >
        </el-pagination>
      </div>
    </div>

    <!-- Details Drawer -->
    <el-drawer
      title="Chi tiết thông tin nhận diện AI"
      :visible.sync="drawerVisible"
      size="50%"
      custom-class="modern-drawer"
    >
      <div v-if="selectedUser" class="drawer-content">
        <!-- Visual Header -->
        <div class="drawer-profile-header">
          <div class="profile-card">
            <el-image
              :src="selectedUser.urlWebFace"
              fit="cover"
              class="large-avatar"
            >
              <div slot="error" class="img-error-placeholder-large">
                <i class="el-icon-user-solid"></i>
              </div>
            </el-image>
            <div class="profile-meta">
              <h2>{{ selectedUser.name }}</h2>
              <div class="flex items-center gap-2 mt-1">
                <el-tag :type="getAccountTagType(selectedUser.typeAccount)" size="medium" effect="dark">
                  {{ getAccountLabel(selectedUser.typeAccount) }}
                </el-tag>
                <el-tag v-if="selectedUser.administrator" type="danger" size="medium" effect="light">
                  Administrator
                </el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- Detail Tabs -->
        <el-tabs type="border-card" class="modern-tabs mt-4">
          <el-tab-pane label="Dữ liệu hình ảnh">
            <div class="single-image-gallery">
              <div class="single-image-box">
                <p class="img-title">Ảnh nhận diện</p>
                <div class="img-preview-container-large">
                  <el-image :src="selectedUser.urlWebFace" fit="contain" class="preview-img-large">
                    <div slot="placeholder" class="img-placeholder"><i class="el-icon-loading"></i></div>
                    <div slot="error" class="img-placeholder"><i class="el-icon-picture-outline"></i></div>
                  </el-image>
                </div>
                <div class="url-copy-box">
                  <el-input size="medium" readonly :value="selectedUser.urlWebFace" placeholder="Chưa có URL">
                    <template #append>
                      <el-button icon="el-icon-document-copy" @click="copyText(selectedUser.urlWebFace)"> Sao chép</el-button>
                    </template>
                  </el-input>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <el-tab-pane label="Dữ liệu thẻ & Vân tay">
            <div class="credentials-info mt-2">
              <div class="section-title"><i class="el-icon-bank-card"></i> Danh sách thẻ nhận diện</div>
              <div class="card-list mt-2">
                <div v-if="!selectedUser.cardRecognitionList || selectedUser.cardRecognitionList.length === 0" class="no-data-alert">
                  Không tìm thấy thẻ nào được đăng ký cho người dùng này.
                </div>
                <div v-else class="card-items">
                  <div v-for="(card, index) in selectedUser.cardRecognitionList" :key="index" class="card-item-tag">
                    <i class="el-icon-bank-card"></i>
                    <span>Số thẻ: <strong>{{ card.cardNumber }}</strong></span>
                  </div>
                </div>
              </div>

              <div class="section-title mt-4"><i class="el-icon-thumb"></i> Danh sách mẫu vân tay</div>
              <div class="fingerprint-list mt-2">
                <div v-if="!selectedUser.fingerRecognitionList || selectedUser.fingerRecognitionList.length === 0" class="no-data-alert">
                  Không tìm thấy mẫu vân tay nào được đăng ký cho người dùng này.
                </div>
                <div v-else class="finger-items">
                  <div v-for="(finger, index) in selectedUser.fingerRecognitionList" :key="index" class="finger-item-tag">
                    <i class="el-icon-thumb"></i>
                    <span>Vị trí ngón: <strong>#{{ finger.fingerIndex }}</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </el-tab-pane>

          <el-tab-pane label="Thiết bị liên kết">
            <div class="device-time-info mt-2">
              <div class="section-title"><i class="el-icon-mobile-phone"></i> Thiết bị nhận diện được gán</div>
              <div class="device-card-large mt-3" v-if="selectedUser.deviceRecognition">
                <div class="device-header">
                  <div class="device-icon-large"><i class="el-icon-s-platform"></i></div>
                  <h3 class="device-title">{{ selectedUser.deviceRecognition.name }}</h3>
                </div>
                
                <div class="device-details-grid">
                  <div class="grid-item">
                    <span class="lbl">Tên miền (Domain)</span>
                    <strong class="val">{{ selectedUser.deviceRecognition.domain || '—' }}</strong>
                  </div>
                  <div class="grid-item">
                    <span class="lbl">Địa chỉ IP</span>
                    <strong class="val">{{ selectedUser.deviceRecognition.ip || '—' }}</strong>
                  </div>
                  <div class="grid-item">
                    <span class="lbl">Cổng cục bộ (Port Local)</span>
                    <strong class="val">{{ selectedUser.deviceRecognition.portLocal || '—' }}</strong>
                  </div>
                  <div class="grid-item">
                    <span class="lbl">Tài khoản thiết bị</span>
                    <strong class="val">{{ selectedUser.deviceRecognition.username || '—' }}</strong>
                  </div>
                  <div class="grid-item full-width">
                    <span class="lbl">Mật khẩu thiết bị</span>
                    <strong class="val code-font">{{ selectedUser.deviceRecognition.password || '—' }}</strong>
                  </div>
                </div>
              </div>
              <div class="no-data-alert mt-2" v-else>
                Chưa gán thiết bị nhận diện nào.
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import ManagerRecognitionService from "@/services/ManagerRecognition/ManagerRecognitionService";

export default {
  name: "CheckRecognition",
  data() {
    return {
      schoolList: [],
      deviceList: [],
      dataTable: [],
      dataFilter: {
        idSchool: "",
        idDevice: "",
        typeAccount: "",
        query: ""
      },
      loading: false,
      drawerVisible: false,
      selectedUser: null,
      
      // Pagination parameters
      pageIndex: 1,
      size: 20,
      totalElements: 0
    };
  },

  computed: {
    stats() {
      let totalCards = 0;
      let totalFingers = 0;
      this.dataTable.forEach(x => {
        totalCards += (x.cardRecognitionList ? x.cardRecognitionList.length : 0);
        totalFingers += (x.fingerRecognitionList ? x.fingerRecognitionList.length : 0);
      });
      return { totalCards, totalFingers };
    }
  },

  created() {
    this.loadSchools();
    this.loadRecognitionData();
  },

  methods: {
    async loadSchools() {
      try {
        const resp = await ManagerRecognitionService.getSchools();
        this.schoolList = resp.data.data || [];
      } catch (err) {
        console.error("Failed to load schools:", err);
      }
    },

    async loadDevices(idSchool) {
      try {
        const resp = await ManagerRecognitionService.getDevicesBySchool(idSchool);
        this.deviceList = resp.data.data || [];
      } catch (err) {
        console.error("Failed to load devices:", err);
        this.deviceList = [];
      }
    },

    async handleSchoolChange(val) {
      this.dataFilter.idDevice = "";
      this.deviceList = [];
      if (val) {
        await this.loadDevices(val);
      }
      this.handleSearch();
    },

    async loadRecognitionData() {
      this.loading = true;
      try {
        const resp = await ManagerRecognitionService.search(
          this.dataFilter.idSchool,
          this.dataFilter.idDevice,
          this.dataFilter.typeAccount,
          this.dataFilter.query,
          this.pageIndex - 1, // 0-indexed parameter for BE
          this.size
        );
        if (resp.data && resp.data.data) {
          const apiData = resp.data.data;
          this.dataTable = apiData.content || [];
          this.totalElements = apiData.totalElements || 0;
        }
      } catch (err) {
        console.error("Failed to load recognition data:", err);
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.pageIndex = 1;
      this.loadRecognitionData();
    },

    resetFilters() {
      this.dataFilter = {
        idSchool: "",
        idDevice: "",
        typeAccount: "",
        query: ""
      };
      this.deviceList = [];
      this.handleSearch();
    },

    handleSizeChange(val) {
      this.size = val;
      this.pageIndex = 1;
      this.loadRecognitionData();
    },

    handleCurrentChange(val) {
      this.pageIndex = val;
      this.loadRecognitionData();
    },

    calculateStt(index) {
      return (this.pageIndex - 1) * this.size + index + 1;
    },

    getAccountLabel(type) {
      const labels = {
        plus: "Nhân viên (Plus)",
        teacher: "Giáo viên",
        parent: "Học sinh",
        student: "Học sinh"
      };
      return labels[type] || type;
    },

    getAccountTagType(type) {
      const types = {
        plus: "primary",
        teacher: "success",
        parent: "warning",
        student: "warning"
      };
      return types[type] || "info";
    },

    handleShowDetail(row) {
      this.selectedUser = row;
      this.drawerVisible = true;
    },

    copyText(text) {
      if (!text) return;
      navigator.clipboard.writeText(text);
      this.$message({
        message: 'Đã sao chép đường dẫn ảnh vào bộ nhớ tạm!',
        type: 'success'
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.ai-check-container {
  padding: 16px;
  background-color: #f4f6fa;
  min-height: calc(100vh - 120px);
}

/* Header Banner Styling */
.ai-header-banner {
  position: relative;
  border-radius: 12px;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  padding: 24px 32px;
  margin-bottom: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(30, 60, 114, 0.15);

  .banner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  }

  .banner-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    z-index: 1;
  }

  .banner-left {
    display: flex;
    align-items: center;
    gap: 20px;
  }

  .ai-icon-pulse {
    width: 60px;
    height: 60px;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid rgba(255, 255, 255, 0.3);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
    animation: iconPulse 3s infinite ease-in-out;

    i {
      font-size: 32px;
      color: #fff;
    }
  }

  .banner-title {
    color: #fff;
    font-size: 22px;
    margin: 0 0 6px 0;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  .banner-subtitle {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    margin: 0;
  }
}

@keyframes iconPulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.2);
  }
  50% {
    transform: scale(1.08);
    box-shadow: 0 0 25px rgba(255, 255, 255, 0.4);
  }
}

/* Stats Cards Grid */
.ai-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  }

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 24px;
  }

  .stat-info {
    flex: 1;
  }

  .stat-label {
    margin: 0 0 4px 0;
    font-size: 13px;
    color: #64748b;
  }

  .stat-value {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
  }

  /* Colors */
  &.blue {
    border-left: 4px solid #3b82f6;
    .stat-icon { background: rgba(59, 130, 246, 0.1); color: #3b82f6; }
  }
  &.green {
    border-left: 4px solid #10b981;
    .stat-icon { background: rgba(16, 185, 129, 0.1); color: #10b981; }
  }
  &.purple {
    border-left: 4px solid #8b5cf6;
    .stat-icon { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; }
  }
}

/* Glassmorphic & Modern Cards */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.05);
  padding: 20px;
}

.table-container {
  background: #fff;
  padding: 12px;
}

/* Modern Form & Inputs */
.modern-filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;

  /deep/ .el-form-item {
    margin-bottom: 0 !important;
    margin-right: 0 !important;
  }

  /deep/ .el-form-item__label {
    font-weight: 600;
    color: #475569;
    padding-bottom: 6px;
  }

  .modern-select {
    width: 220px;
  }

  .modern-input {
    width: 240px;
  }

  .filter-actions {
    margin-left: auto;
  }
}

/* Modern Table Styles */
.modern-table {
  border-radius: 8px;
  overflow: hidden;

  /deep/ th {
    background-color: #f8fafc !important;
    color: #475569;
    font-weight: 600;
    height: 48px;
  }

  /deep/ td {
    padding: 10px 0;
  }
}

/* Cell UI Elements */
.user-profile-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  .user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    border: 2px solid #e2e8f0;
    box-shadow: 0 2px 5px rgba(0,0,0,0.05);
    display: block;
    overflow: hidden;
  }

  .img-error-placeholder {
    width: 100%;
    height: 100%;
    background-color: #e2e8f0;
    color: #94a3b8;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
  }

  .user-details {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .user-name {
    font-weight: 600;
    color: #1e293b;
    font-size: 14px;
  }

  .user-tag {
    font-size: 11px;
    height: 18px;
    line-height: 16px;
    padding: 0 6px;
    border: none;
    border-radius: 4px;
  }
}

.school-name-text {
  font-weight: 600;
  color: #3b82f6;
}

.device-name-text {
  font-weight: 500;
  color: #334155;
}

.info-list {
  font-size: 12px;
  color: #64748b;
  line-height: 1.6;

  .info-lbl {
    color: #94a3b8;
  }
}

.credentials-badges {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;

  .badge-item {
    font-size: 11px;
    color: #cbd5e1;
    display: flex;
    align-items: center;
    gap: 4px;
    font-weight: 500;

    i {
      font-size: 13px;
    }

    &.active {
      color: #3b82f6;
      font-weight: 600;
    }
  }
}

.time-range {
  font-size: 12px;
  color: #475569;
  line-height: 1.4;

  .time-separator {
    color: #94a3b8;
    font-size: 10px;
    margin: 2px 0;
    text-transform: uppercase;
  }
}

/* Drawer UI Styling */
.modern-drawer {
  /deep/ .el-drawer__header {
    margin-bottom: 0px;
    padding: 16px 24px;
    font-weight: 700;
    color: #1e293b;
    border-bottom: 1px solid #e2e8f0;
  }

  .drawer-content {
    padding: 24px;
    height: calc(100vh - 80px);
    overflow-y: auto;
  }

  .drawer-profile-header {
    background: #f8fafc;
    border-radius: 12px;
    padding: 20px;
    border: 1px solid #e2e8f0;

    .profile-card {
      display: flex;
      align-items: center;
      gap: 20px;
    }

    .large-avatar {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      border: 3px solid #fff;
      box-shadow: 0 4px 10px rgba(0,0,0,0.1);
      display: block;
      overflow: hidden;
    }

    .img-error-placeholder-large {
      width: 100%;
      height: 100%;
      background-color: #e2e8f0;
      color: #94a3b8;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 40px;
    }

    .profile-meta h2 {
      margin: 0;
      font-size: 18px;
      color: #0f172a;
    }
  }
}

/* Image gallery with only web face */
.single-image-gallery {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 0;

  .single-image-box {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    width: 320px;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  }

  .img-title {
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 12px 0;
  }

  .img-preview-container-large {
    width: 100%;
    height: 280px;
    background: #f1f5f9;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .preview-img-large {
    width: 100%;
    height: 100%;
  }

  .img-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #94a3b8;
    font-size: 32px;
    background: #f1f5f9;
  }
}

.credentials-info {
  .section-title {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    border-left: 3px solid #3b82f6;
    padding-left: 8px;
  }

  .no-data-alert {
    background: #f8fafc;
    padding: 12px;
    border-radius: 6px;
    font-size: 12px;
    color: #94a3b8;
    text-align: center;
    border: 1px dashed #e2e8f0;
  }

  .card-items, .finger-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .card-item-tag, .finger-item-tag {
    background: #f0fdfa;
    border: 1px solid #ccfbf1;
    color: #0f766e;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .finger-item-tag {
    background: #faf5ff;
    border-color: #f3e8ff;
    color: #6b21a8;
  }
}

.device-time-info {
  .section-title {
    font-size: 13px;
    font-weight: 700;
    color: #475569;
    border-left: 3px solid #3b82f6;
    padding-left: 8px;
  }

  .device-card-large {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.02);

    .device-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e2e8f0;
    }

    .device-icon-large {
      font-size: 28px;
      color: #3b82f6;
    }

    .device-title {
      margin: 0;
      font-size: 16px;
      font-weight: 700;
      color: #1e293b;
    }
  }

  .device-details-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    .grid-item {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &.full-width {
        grid-column: span 2;
      }

      .lbl {
        font-size: 11px;
        color: #64748b;
        text-transform: uppercase;
        font-weight: 600;
      }

      .val {
        font-size: 14px;
        color: #1e293b;

        &.code-font {
          font-family: 'Courier New', Courier, monospace;
          background: #f1f5f9;
          padding: 2px 6px;
          border-radius: 4px;
          display: inline-block;
          width: fit-content;
        }
      }
    }
  }
}

.w-full {
  width: 100%;
}
</style>
